import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDublinDate, calculateAge } from '../utils/date';
import { studentDisplayName } from '../utils/student';
import { useAuth } from '../hooks/useAuth';
import { useLocationContext } from '../hooks/useLocationContext';
import { ScopedLink, useScopedPath } from '../components/ScopedLink';
import {
  useLocalGetStudentsByParentQuery,
  useLocalGetStudentsQuery,
  useUpdateStudentMutation,
  useMarkStudentLeftMutation,
  useReactivateStudentMutation,
  useScheduleStudentLeavingMutation,
  useDeleteStudentMutation,
  useListSessionsForLocationQuery,
} from '../store/api/localApi';
import ObservationCreateModal from '../components/observations/ObservationCreateModal';
import IncidentCreateModal from '../components/incidents/IncidentCreateModal';
import { SessionStatus } from '../types/attendance-v2';
import RoleGate from '../components/RoleGate';
import RegistrationPendingCard from '../components/RegistrationPendingCard';
import RegistrationStatusBadge from '../components/RegistrationStatusBadge';
import ParentOnboardingCard from '../components/ParentOnboardingCard';
import { RegistrationSection } from '../components/students/RegistrationSection';
import { StudentParentsSection } from '../components/students/StudentParentsSection';
import { IntakeFormsSection } from '../components/students/IntakeFormsSection';
import { StudentPoliciesSection } from '../components/students/StudentPoliciesSection';
import EnrollmentHistorySection from './students/EnrollmentHistorySection';
import ObservationsAuditSection from '../components/observations/ObservationsAuditSection';
import IncidentHistorySection from '../components/incidents/IncidentHistorySection';
import StudentLegalHoldPanel from '../components/retention/StudentLegalHoldPanel';
import LearningGoalCoverage from '../components/learning-goals/LearningGoalCoverage';
import { FeesSection } from '../components/students/FeesSection';
import { StudentAttendanceSection } from '../components/students/StudentAttendanceSection';
import MessageParentsButton from '../components/students/MessageParentsButton';
import {
  PencilIcon,
  TrashIcon,
  ArrowLeftIcon,
  PhoneIcon,
  EnvelopeIcon,
  CakeIcon,
  AcademicCapIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { useToast } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { GRADE_ORDER } from '../types';

const StudentDetail: React.FC = () => {
  const { showError, showSuccess } = useToast();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isParent, isStaff, isAdmin, isOrgLevel, isStaffOrAbove } = useAuth();
  const { effectiveLocationId: userLocation, urlPrefix, effectiveOrgId } = useLocationContext();
  const scopedPath = useScopedPath();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, string>>({});
  // Nested gp/allergies fields can't live in editForm (Record<string, string>
  // can't hold booleans or nested objects), so they get their own slice and
  // are merged into the same save call in save-student-btn's onClick below.
  const [medicalEdit, setMedicalEdit] = useState<{
    gp: { name: string; address: string; phone: string };
    allergies: { hasAny: boolean; detail: string };
  }>({
    gp: { name: '', address: '', phone: '' },
    allergies: { hasAny: false, detail: '' },
  });

  const { data: parentStudents } = useLocalGetStudentsByParentQuery(user?.id || '', {
    skip: !isParent || !user?.id,
  });
  const { data: allStudents } = useLocalGetStudentsQuery(userLocation || '', {
    skip: isParent || (!userLocation && !isOrgLevel),
  });

  const students = isParent ? parentStudents : allStudents;
  const [updateStudent, { isLoading: isUpdating }] = useUpdateStudentMutation();
  const [markStudentLeft, { isLoading: isMarkingLeft }] = useMarkStudentLeftMutation();
  const [reactivateStudent, { isLoading: isReactivating }] = useReactivateStudentMutation();
  const [scheduleStudentLeaving, { isLoading: isScheduling }] = useScheduleStudentLeavingMutation();
  const [deleteStudent, { isLoading: isDeleting }] = useDeleteStudentMutation();
  const [showMarkLeftConfirm, setShowMarkLeftConfirm] = useState(false);
  // Departure date for the mark-left dialog. Defaults to today (Dublin civil
  // date); the operator can back-date it. Server clamps per-enrolment.
  const [leftAtDate, setLeftAtDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [showScheduleLeaving, setShowScheduleLeaving] = useState(false);
  // Expected last day for the schedule-leaving dialog. Defaults to today; the
  // server rejects a strictly-past date (that path is mark-left, not scheduling).
  const [expectedEndDate, setExpectedEndDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const student = students?.find((s) => s.id === id);
  // A parent's `students` list comes from the edge-based getStudentsByParent
  // (GSI2 STUDENT_PARENT rows), so it already includes children where this user
  // is a CO-parent, not just the primary `student.parentId`. Deriving ownership
  // from `!!student` therefore covers both; comparing `student.parentId ===
  // user.id` would wrongly lock the second parent out (they'd see the child but
  // not their own info / edit).
  const isParentOfThisChild = isParent && !!student;

  // Self-view: show the viewing parent their own info. This card is
  // parent-facing; staff and admins get the full parent list, with contact
  // details, from StudentParentsSection below.
  const parent = isParentOfThisChild ? user : null;

  // Admins edit any student; a parent edits their own child (primary or co).
  // Staff have no student:update grant on the backend, so no Edit button.
  const canEdit = isAdmin || isParentOfThisChild;

  // Today's eligible sessions for this kid: sessions at their location, on
  // today's date, where the student is expected AND the session is either
  // IN_PROGRESS or recently ENDED (within the 2h backfill window the backend
  // enforces). The query is gated on staff/admin role + a resolvable
  // locationId so parents and unauth contexts don't fetch it.
  const todayIso = new Date().toISOString().slice(0, 10);
  const observeLocationId = student?.locationId ?? '';
  const canObserveAtAll = (isAdmin || isStaff) && !!observeLocationId;
  const { data: todaysSessionsData } = useListSessionsForLocationQuery(
    { locationId: observeLocationId, date: todayIso },
    { skip: !canObserveAtAll }
  );
  const eligibleSessions = React.useMemo(() => {
    if (!student || !todaysSessionsData) return [];
    const cutoffMs = 2 * 60 * 60 * 1000;
    const now = Date.now();
    return todaysSessionsData.sessions.filter((s) => {
      if (!s.expectedStudentIds.includes(student.id)) return false;
      if (s.status === SessionStatus.IN_PROGRESS) return true;
      if (s.status === SessionStatus.ENDED && s.endedAt) {
        return now <= new Date(s.endedAt).getTime() + cutoffMs;
      }
      return false;
    });
  }, [student, todaysSessionsData]);

  const [observePickedSessionId, setObservePickedSessionId] = useState<string | null>(null);
  const [observeOpen, setObserveOpen] = useState(false);
  const [reportIncidentOpen, setReportIncidentOpen] = useState(false);

  if (!student) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="mt-2 text-sm font-medium text-gray-900">Student not found</h3>
            <p className="mt-1 text-sm text-gray-500">
              The student you're looking for doesn't exist.
            </p>
            <div className="mt-6">
              <ScopedLink
                to="/students"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <ArrowLeftIcon className="-ml-1 mr-2 h-5 w-5" />
                Back to Students
              </ScopedLink>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // For parents on a PENDING child, the only thing to show is the registration
  // CTA — every other action is gated until they finish the form. Staff/admin
  // see the full screen with a pending badge instead.
  const isPending = (student.registrationStatus || 'PENDING') === 'PENDING';
  if (isParent && isPending) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(scopedPath('/students'))}
            className="mb-6 inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to children
          </button>
          <RegistrationPendingCard student={student} />
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(scopedPath('/students'))}
                className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                Back to Students
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* Message parents — staff/admin only, shown regardless of canEdit */}
              {(isAdmin || isStaff) && (
                <MessageParentsButton
                  studentId={student.id}
                  studentName={studentDisplayName(student) || 'this student'}
                />
              )}
              {(isAdmin || isStaff) && eligibleSessions.length > 0 && (
                <button
                  type="button"
                  data-testid="student-detail-observe-btn"
                  onClick={() => {
                    setObservePickedSessionId(eligibleSessions[0].id);
                    setObserveOpen(true);
                  }}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-primary-600 text-white text-sm hover:bg-primary-700"
                >
                  + Observation
                </button>
              )}
              {(isAdmin || isStaff) && eligibleSessions.length === 0 && (
                <button
                  type="button"
                  data-testid="student-detail-observe-btn"
                  disabled
                  title="No active session for this student right now"
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-gray-200 text-gray-400 text-sm cursor-not-allowed"
                >
                  + Observation
                </button>
              )}
              {/* Unlike observations, incidents don't need a live session —
                  they happen at arrival, collection, on trips. No session link
                  is sent. Hidden for departed children: same rule as the
                  register's child picker (you can't raise a fresh incident
                  against a child who has left); their incident history below
                  stays readable. */}
              {(isAdmin || isStaff) && student.status !== 'LEFT' && (
                <button
                  type="button"
                  data-testid="student-detail-report-incident-btn"
                  onClick={() => setReportIncidentOpen(true)}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-gray-300 bg-white text-gray-700 text-sm hover:bg-gray-50"
                >
                  Report incident
                </button>
              )}
            </div>
            {canEdit && (
              <div className="flex flex-wrap gap-2">
                {/* Edit hidden until the parent has completed registration —
                    no point editing a row whose canonical values are about to
                    arrive from the form. Staff can still remove the row. */}
                {!isPending && (
                  <button
                    data-testid="edit-student-btn"
                    onClick={() => {
                      if (isEditing) {
                        setIsEditing(false);
                      } else {
                        setEditForm({
                          firstName: student.firstName || '',
                          lastName: student.lastName || '',
                          dateOfBirth: student.dateOfBirth || '',
                          grade: student.grade || '',
                        });
                        setMedicalEdit({
                          gp: {
                            name: student.gp?.name || '',
                            address: student.gp?.address || '',
                            phone: student.gp?.phone || '',
                          },
                          allergies: {
                            hasAny: student.allergies?.hasAny || false,
                            detail: student.allergies?.detail || '',
                          },
                        });
                        setIsEditing(true);
                      }
                    }}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <PencilIcon className="-ml-0.5 mr-2 h-4 w-4" />
                    {isEditing ? 'Cancel' : 'Edit'}
                  </button>
                )}
                <RoleGate when={isAdmin}>
                  {student.status === 'LEFT' ? (
                    <button
                      data-testid="student-reactivate"
                      onClick={async () => {
                        try {
                          await reactivateStudent(student.id).unwrap();
                        } catch (err: any) {
                          showError(
                            err.data?.error || err.data?.message || 'Failed to reactivate student'
                          );
                        }
                      }}
                      disabled={isReactivating}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                    >
                      {isReactivating ? 'Reactivating...' : 'Reactivate'}
                    </button>
                  ) : (
                    <button
                      data-testid="student-mark-left"
                      onClick={() => {
                        setLeftAtDate(new Date().toISOString().slice(0, 10));
                        setShowMarkLeftConfirm(true);
                      }}
                      disabled={isMarkingLeft}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                    >
                      <TrashIcon className="-ml-0.5 mr-2 h-4 w-4" />
                      {/* A child who never started has no enrollments to end — the
                          action is cancelling their registration, not recording a leave. */}
                      {student.status === 'PENDING_START'
                        ? isMarkingLeft
                          ? 'Cancelling...'
                          : 'Cancel registration'
                        : isMarkingLeft
                        ? 'Marking left...'
                        : 'Mark left'}
                    </button>
                  )}
                  {/* Schedule a future departure (set expectedEndDate) for a
                      student who is still with the service. A LEFT child has
                      already gone, so the action is hidden for them. */}
                  {student.status !== 'LEFT' && (
                    <button
                      data-testid="student-schedule-leaving"
                      onClick={() => {
                        setExpectedEndDate(
                          student.expectedEndDate || new Date().toISOString().slice(0, 10)
                        );
                        setShowScheduleLeaving(true);
                      }}
                      disabled={isScheduling}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      <CalendarDaysIcon className="-ml-0.5 mr-2 h-4 w-4" />
                      {student.expectedEndDate ? 'Change leaving date' : 'Schedule leaving'}
                    </button>
                  )}
                  {student.registrationStatus === 'PENDING' && (
                    <button
                      data-testid="delete-student-btn"
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={isDeleting}
                      title="Permanently delete this pending registration"
                      className="inline-flex items-center px-3 py-2 border border-red-700 text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                    >
                      <TrashIcon className="-ml-0.5 mr-2 h-4 w-4" />
                      {isDeleting ? 'Deleting...' : 'Delete permanently'}
                    </button>
                  )}
                </RoleGate>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Info */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex flex-col sm:flex-row items-center sm:space-x-6 space-y-4 sm:space-y-0">
                  <div className="h-24 w-24 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-3xl font-medium text-primary-600">
                      {student.firstName?.[0] ?? '?'}
                      {student.lastName?.[0] ?? ''}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h1 className="text-3xl font-bold text-gray-900">
                        {studentDisplayName(student)}
                      </h1>
                      <RegistrationStatusBadge student={student} />
                      {student.status === 'LEFT' && (
                        <span
                          data-testid="student-left-badge"
                          className="inline-flex items-center rounded-full bg-gray-200 px-2.5 py-0.5 text-xs font-medium text-gray-700"
                        >
                          Left{student.leftAt ? ` · ${student.leftAt}` : ''}
                        </span>
                      )}
                      {student.status !== 'LEFT' && student.expectedEndDate && (
                        <span
                          data-testid="student-leaving-badge"
                          className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800"
                        >
                          Leaving on {student.expectedEndDate}
                        </span>
                      )}
                      {student.legalHold && (
                        <span
                          data-testid="student-legal-hold-name-badge"
                          className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800"
                        >
                          Legal hold · clocks frozen
                        </span>
                      )}
                    </div>
                    <p className="text-lg text-gray-600">
                      {student.grade || 'Awaiting registration'}
                    </p>
                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                      {(() => {
                        const age = calculateAge(student.dateOfBirth);
                        return age !== null ? (
                          <div className="flex items-center">
                            <CakeIcon className="mr-1 h-4 w-4" />
                            Age {age}
                          </div>
                        ) : null;
                      })()}
                      <div className="flex items-center">
                        <AcademicCapIcon className="mr-1 h-4 w-4" />
                        Student ID: {student.id}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Registration snapshot — only meaningful for staff/admin
                    viewing a COMPLETE child; the parent already has their
                    own copy from the fill flow. */}
                {!isParent && (
                  <div className="mt-8">
                    <RegistrationSection student={student} />
                    <IntakeFormsSection
                      studentId={student.id}
                      studentName={studentDisplayName(student) || 'this child'}
                      isAdmin={isAdmin}
                    />
                    {/* Staff only: parents see their child's current classes, not
                        who changed the enrolment and when. */}
                    <EnrollmentHistorySection studentId={student.id} />
                    <StudentPoliciesSection studentId={student.id} />
                    <ObservationsAuditSection studentId={student.id} detailHrefBase={urlPrefix} />
                    <IncidentHistorySection studentId={student.id} detailHrefBase={urlPrefix} />
                    {/* Retention legal hold (Task 21) — admin-only; freezes the
                        child's retention clocks. Nothing is deleted. */}
                    <RoleGate when={isAdmin}>
                      <StudentLegalHoldPanel student={student} />
                    </RoleGate>
                  </div>
                )}

                {/* Student Details */}
                <div className="mt-8">
                  <h3
                    data-testid="student-info-heading"
                    className="text-lg leading-6 font-medium text-gray-900"
                  >
                    Student Information
                  </h3>
                  <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">First Name</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.firstName || ''}
                          onChange={(e) =>
                            setEditForm((p) => ({ ...p, firstName: e.target.value }))
                          }
                          className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-900">
                          {student.firstName || (
                            <span className="text-gray-400 italic">Awaiting registration</span>
                          )}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Last Name</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.lastName || ''}
                          onChange={(e) => setEditForm((p) => ({ ...p, lastName: e.target.value }))}
                          className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-900">
                          {student.lastName || (
                            <span className="text-gray-400 italic">Awaiting registration</span>
                          )}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Date of Birth
                      </label>
                      {isEditing ? (
                        <input
                          type="date"
                          value={editForm.dateOfBirth || ''}
                          onChange={(e) =>
                            setEditForm((p) => ({ ...p, dateOfBirth: e.target.value }))
                          }
                          className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-900">
                          {student.dateOfBirth ? (
                            formatDublinDate(student.dateOfBirth)
                          ) : (
                            <span className="text-gray-400 italic">Awaiting registration</span>
                          )}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Grade</label>
                      {isEditing ? (
                        <select
                          data-testid="grade-select"
                          value={editForm.grade || ''}
                          onChange={(e) => setEditForm((p) => ({ ...p, grade: e.target.value }))}
                          className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        >
                          <option value="">Not set</option>
                          {GRADE_ORDER.map((g) => (
                            <option key={g} value={g}>
                              {g}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="mt-1 text-sm text-gray-900">
                          {student.grade || (
                            <span className="text-gray-400 italic">Awaiting registration</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>

                  {isEditing && (
                    <div className="mt-6 flex justify-end space-x-3">
                      <button
                        data-testid="cancel-edit-btn"
                        onClick={() => setIsEditing(false)}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        data-testid="save-student-btn"
                        disabled={isUpdating}
                        onClick={async () => {
                          // Conditional-required: allergy detail must be filled whenever
                          // the toggle is on, mirroring the registration form's
                          // requiredWhen behaviour for the same field. requiredWhen
                          // itself is a dynamic-form-builder mechanism (FormFill.tsx)
                          // not wired into this hand-written page, so this matches the
                          // guard-clause + showError + early-return precedent already
                          // used elsewhere in this codebase (see Profile.tsx's password-
                          // confirmation check) rather than inventing a new idiom.
                          //
                          // Only enforced when THIS edit is what leaves the record in
                          // that state. A pre-existing row can already be
                          // `hasAny: true` with no `detail` (Task 2's schema allows it,
                          // and it's the exact shape `setMedicalEdit`'s seed carries
                          // over unchanged if the admin never touches this section) --
                          // blocking save in that case would make an unrelated edit
                          // (e.g. fixing a typo'd surname) permanently unsavable unless
                          // the admin invents allergy text they have no information
                          // for. So skip the guard when the seeded student record was
                          // ALREADY in the same hasAny-true/no-detail state and the
                          // admin hasn't changed hasAny or added detail since.
                          const allergyStateUnchangedFromLoad =
                            !!student.allergies?.hasAny &&
                            !student.allergies?.detail &&
                            medicalEdit.allergies.hasAny === student.allergies.hasAny &&
                            !medicalEdit.allergies.detail.trim();
                          if (
                            medicalEdit.allergies.hasAny &&
                            !medicalEdit.allergies.detail.trim() &&
                            !allergyStateUnchangedFromLoad
                          ) {
                            showError('Please describe the allergy before saving.');
                            return;
                          }
                          try {
                            const updates: Record<string, unknown> = { ...editForm };
                            if (!updates.grade) delete updates.grade;
                            // gp is only sent (as a whole object) when at least one
                            // field has a value, so an admin editing only e.g. grade
                            // doesn't send an empty gp object -- this mirrors how
                            // `grade` above is conditionally deleted when unset. Note
                            // the omit-when-empty treatment is NOT applied per-field
                            // inside gp itself: name/phone are always sent as-is
                            // (possibly '') once any field is non-empty, while address
                            // is spread in only when non-empty. That's intentional --
                            // name/phone are non-optional in the UI Student type, so
                            // sending '' for an untouched one is the accurate
                            // "cleared" value, whereas address is optional and should
                            // be omitted rather than sent as ''. Not strictly required
                            // now that gp.name/gp.phone are .optional() server-side
                            // (Task 2), but kept for consistency with the existing
                            // style in this handler.
                            if (
                              medicalEdit.gp.name ||
                              medicalEdit.gp.phone ||
                              medicalEdit.gp.address
                            ) {
                              updates.gp = {
                                name: medicalEdit.gp.name,
                                phone: medicalEdit.gp.phone,
                                ...(medicalEdit.gp.address
                                  ? { address: medicalEdit.gp.address }
                                  : {}),
                              };
                            }
                            // allergies.hasAny is always sent (never conditionally
                            // omitted) since it's a real boolean that can legitimately
                            // flip from true to false -- omitting it when false would
                            // never let someone clear a previously-set allergy flag.
                            updates.allergies = {
                              hasAny: medicalEdit.allergies.hasAny,
                              ...(medicalEdit.allergies.detail
                                ? { detail: medicalEdit.allergies.detail }
                                : {}),
                            };
                            await updateStudent({ id: student.id, updates }).unwrap();
                            setIsEditing(false);
                          } catch (err: any) {
                            showError(
                              err.data?.error || err.data?.message || 'Failed to update student'
                            );
                          }
                        }}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                      >
                        {isUpdating ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Medical & Allergies — always visible (staff/admin/the
                    child's own parent all reach this page with read access);
                    the edit affordance is gated on the same isEditing session
                    as name/DOB/grade above, so canEdit implicitly controls it
                    (staff never sees edit-student-btn, so isEditing can never
                    become true for them here). */}
                <div className="mt-8" data-testid="student-medical-contact-section">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Medical &amp; Allergies
                  </h3>
                  <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Family doctor name
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          data-testid="student-gp-name"
                          value={medicalEdit.gp.name}
                          onChange={(e) =>
                            setMedicalEdit((p) => ({ ...p, gp: { ...p.gp, name: e.target.value } }))
                          }
                          className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-900" data-testid="student-gp-name">
                          {student.gp?.name || (
                            <span className="text-gray-400 italic">Not provided</span>
                          )}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Family doctor phone
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          data-testid="student-gp-phone"
                          value={medicalEdit.gp.phone}
                          onChange={(e) =>
                            setMedicalEdit((p) => ({
                              ...p,
                              gp: { ...p.gp, phone: e.target.value },
                            }))
                          }
                          className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-900" data-testid="student-gp-phone">
                          {student.gp?.phone ? (
                            <a
                              href={`tel:${student.gp.phone}`}
                              className="text-primary-600 hover:underline"
                            >
                              {student.gp.phone}
                            </a>
                          ) : (
                            <span className="text-gray-400 italic">Not provided</span>
                          )}
                        </p>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Family doctor address
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          data-testid="student-gp-address"
                          value={medicalEdit.gp.address}
                          onChange={(e) =>
                            setMedicalEdit((p) => ({
                              ...p,
                              gp: { ...p.gp, address: e.target.value },
                            }))
                          }
                          className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-900" data-testid="student-gp-address">
                          {student.gp?.address || (
                            <span className="text-gray-400 italic">Not provided</span>
                          )}
                        </p>
                      )}
                    </div>
                    {student.gp?.audit && (
                      <p
                        className="sm:col-span-2 text-xs text-gray-400"
                        data-testid="student-gp-audit"
                      >
                        Updated {formatDublinDate(student.gp.audit.lastUpdatedAt)} by{' '}
                        {student.gp.audit.lastUpdatedByRole}
                      </p>
                    )}

                    <div className="sm:col-span-2 border-t border-gray-100 pt-4">
                      <label className="block text-sm font-medium text-gray-700">Allergies</label>
                      {isEditing ? (
                        <div className="mt-1 space-y-2">
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              data-testid="student-allergy-toggle"
                              checked={medicalEdit.allergies.hasAny}
                              onChange={(e) =>
                                setMedicalEdit((p) => ({
                                  ...p,
                                  allergies: { ...p.allergies, hasAny: e.target.checked },
                                }))
                              }
                            />
                            Has allergies
                          </label>
                          {medicalEdit.allergies.hasAny && (
                            <textarea
                              data-testid="student-allergy-detail"
                              value={medicalEdit.allergies.detail}
                              onChange={(e) =>
                                setMedicalEdit((p) => ({
                                  ...p,
                                  allergies: { ...p.allergies, detail: e.target.value },
                                }))
                              }
                              rows={2}
                              className="block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            />
                          )}
                        </div>
                      ) : (
                        <p className="mt-1 text-sm text-gray-900">
                          {student.allergies?.hasAny ? (
                            <>
                              <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                                Yes — has allergies
                              </span>
                              {student.allergies.detail && (
                                <span className="block mt-1" data-testid="student-allergy-detail">
                                  {student.allergies.detail}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400 italic">No known allergies</span>
                          )}
                        </p>
                      )}
                    </div>
                    {student.allergies?.audit && (
                      <p
                        className="sm:col-span-2 text-xs text-gray-400"
                        data-testid="student-allergy-audit"
                      >
                        Updated {formatDublinDate(student.allergies.audit.lastUpdatedAt)} by{' '}
                        {student.allergies.audit.lastUpdatedByRole}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-8">
                  <FeesSection studentId={student.id} isAdmin={isAdmin} />
                </div>
                {/* Admin-only, and gated here rather than inside the component
                    so a non-admin never mounts it: the attendance-history
                    endpoint 403s STAFF and PARENT — a parent included, even on
                    their own child. */}
                <RoleGate when={isAdmin}>
                  <StudentAttendanceSection studentId={student.id} />
                </RoleGate>
                {/* Aistéar learning-goal coverage — under-6 only. Gate on the
                    child's DOB here so an over-6 child never fires the query;
                    the component also trusts the endpoint's `eligible` flag. */}
                {(() => {
                  const age = calculateAge(student.dateOfBirth);
                  return age !== null && age < 6 ? (
                    <LearningGoalCoverage studentId={student.id} />
                  ) : null;
                })()}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Parent Information */}
            {parent && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Parent Information
                  </h3>
                  <div className="mt-4">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-600">
                          {parent.firstName[0]}
                          {parent.lastName[0]}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {parent.firstName} {parent.lastName}
                        </p>
                        <div className="flex items-center text-sm text-gray-500">
                          <EnvelopeIcon className="mr-1 h-4 w-4" />
                          {parent.email}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Who the parents are + how to reach them (staff read; admin can
                also correct phone/address — STAFF stays read-only) */}
            <StudentParentsSection
              studentId={student.id}
              childName={student.firstName || 'this child'}
              isStaffOrAbove={isStaffOrAbove}
              isAdmin={isAdmin}
            />

            {/* Parent onboarding (admin-only, hidden when complete) */}
            <RoleGate when={isAdmin}>
              <ParentOnboardingCard studentId={student.id} />
            </RoleGate>

            {/* Emergency Contact */}
            {student.emergencyContact && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Emergency Contact</h3>
                  <div className="mt-4 space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {student.emergencyContact.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {student.emergencyContact.relationship}
                      </p>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <PhoneIcon className="mr-2 h-4 w-4" />
                      <a
                        href={`tel:${student.emergencyContact.phone}`}
                        className="hover:text-primary-600"
                      >
                        {student.emergencyContact.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ConfirmDialog
        isOpen={showMarkLeftConfirm}
        onConfirm={async () => {
          setShowMarkLeftConfirm(false);
          try {
            await markStudentLeft({
              id: student.id,
              leftAt: leftAtDate,
              // `reason` is the free-text audit note on the lifecycle record.
              // A pre-start child never attended, so record the cancellation
              // rather than a leave.
              reason:
                student.status === 'PENDING_START'
                  ? 'Registration cancelled before start'
                  : undefined,
            }).unwrap();
            navigate(scopedPath('/students'));
          } catch (err: any) {
            showError(err.data?.error || err.data?.message || 'Failed to mark student as left');
          }
        }}
        onCancel={() => setShowMarkLeftConfirm(false)}
        title={student.status === 'PENDING_START' ? 'Cancel registration' : 'Mark student as left'}
        message={
          student.status === 'PENDING_START'
            ? `Cancel ${studentDisplayName(
                student
              )}'s registration. They never started, so there are no enrollments to end. This removes them from the active roster; their record is kept.`
            : `Mark ${studentDisplayName(
                student
              )} as having left the service. This ends their current class enrollments on the date below and cancels any future bookings. Their attendance history is kept.`
        }
        confirmLabel={student.status === 'PENDING_START' ? 'Cancel registration' : 'Mark left'}
        confirmTestId="student-mark-left-confirm"
        variant="danger"
        isLoading={isMarkingLeft}
      >
        {/* A pre-start child has no last day to pick — the cancellation date is
            irrelevant since there are no enrollments to end. */}
        {student.status !== 'PENDING_START' && (
          <div className="mt-3">
            <label
              htmlFor="student-mark-left-date"
              className="block text-sm font-medium text-gray-700"
            >
              Last day
            </label>
            <input
              id="student-mark-left-date"
              type="date"
              data-testid="student-mark-left-date"
              value={leftAtDate}
              onChange={(e) => setLeftAtDate(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>
        )}
      </ConfirmDialog>
      <ConfirmDialog
        isOpen={showScheduleLeaving}
        onConfirm={async () => {
          setShowScheduleLeaving(false);
          try {
            await scheduleStudentLeaving({
              id: student.id,
              expectedEndDate,
            }).unwrap();
            showSuccess('Leaving date scheduled');
          } catch (err: any) {
            showError(err.data?.error || err.data?.message || 'Failed to schedule leaving date');
          }
        }}
        onCancel={() => setShowScheduleLeaving(false)}
        title="Schedule leaving"
        message={`Set ${studentDisplayName(
          student
        )}'s last expected day. They stay active until that date, then leave automatically. Their attendance history is kept.`}
        confirmLabel="Schedule"
        confirmTestId="student-schedule-leaving-confirm"
        variant="default"
        isLoading={isScheduling}
      >
        <div className="mt-3">
          <label
            htmlFor="student-schedule-leaving-date"
            className="block text-sm font-medium text-gray-700"
          >
            Last expected day
          </label>
          <input
            id="student-schedule-leaving-date"
            type="date"
            data-testid="student-schedule-leaving-date"
            value={expectedEndDate}
            onChange={(e) => setExpectedEndDate(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
          {student.expectedEndDate && (
            <button
              type="button"
              data-testid="student-clear-scheduled-leaving"
              disabled={isScheduling}
              onClick={async () => {
                setShowScheduleLeaving(false);
                try {
                  await scheduleStudentLeaving({
                    id: student.id,
                    expectedEndDate: null,
                  }).unwrap();
                  showSuccess('Scheduled leaving cleared');
                } catch (err: any) {
                  showError(
                    err.data?.error || err.data?.message || 'Failed to clear scheduled leaving'
                  );
                }
              }}
              className="mt-3 text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
            >
              Clear scheduled leaving
            </button>
          )}
        </div>
      </ConfirmDialog>
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onConfirm={async () => {
          setShowDeleteConfirm(false);
          try {
            await deleteStudent(student.id).unwrap();
            navigate(scopedPath('/students'));
          } catch (err: any) {
            showError(err.data?.error || err.data?.message || 'Failed to delete student');
          }
        }}
        onCancel={() => setShowDeleteConfirm(false)}
        title="Delete permanently"
        message={`Permanently delete ${studentDisplayName(
          student
        )}? This removes the student record, any class enrollments, and the parent's pending invitation. If the parent has no other children on file, their account will also be removed. This cannot be undone.`}
        confirmLabel="Delete permanently"
        variant="danger"
      />
      {reportIncidentOpen && (student.organizationId ?? effectiveOrgId) && (
        <IncidentCreateModal
          open
          onClose={() => setReportIncidentOpen(false)}
          studentId={student.id}
          studentName={studentDisplayName(student) || 'this child'}
          locationId={student.locationId}
          organizationId={(student.organizationId ?? effectiveOrgId)!}
        />
      )}
      {observeOpen &&
        observePickedSessionId &&
        (() => {
          const picked =
            eligibleSessions.find((s) => s.id === observePickedSessionId) ?? eligibleSessions[0];
          if (!picked) return null;
          return (
            <ObservationCreateModal
              open
              onClose={() => setObserveOpen(false)}
              studentId={student.id}
              studentName={studentDisplayName(student) || 'this student'}
              studentDateOfBirth={student.dateOfBirth}
              session={{
                id: picked.id,
                className: picked.className,
                scheduledStart: picked.scheduledStart,
                scheduledEnd: picked.scheduledEnd,
                date: picked.date,
              }}
              sessionOptions={
                eligibleSessions.length > 1
                  ? eligibleSessions.map((s) => ({
                      id: s.id,
                      className: s.className,
                      scheduledStart: s.scheduledStart,
                      scheduledEnd: s.scheduledEnd,
                      date: s.date,
                    }))
                  : undefined
              }
              onSessionChange={(id) => setObservePickedSessionId(id)}
            />
          );
        })()}
    </div>
  );
};

export default StudentDetail;
